import useI18n from '@/shared/hooks/usei18n'
import { Button } from '@/common/components/Button/Button'
import { Checkbox } from '@/common/components/Checkbox/Checkbox'
import { GenericDialog } from '@/common/components/GenericDialog/GenericDialog'
import { RadioGroup, Spinner, Table, TableCell, TableColumnHeader, TableRow } from '@/common'
import type { CbamReportingDataExportFormat } from '@/config/cbamReportingPeriods'
import { useGetOfficial2026EuaEmissionPrice } from '@/shared/hooks/euaEmissionPriceOverview'
import {
  OFFICIAL_EUA_2026_QUARTERS_WITH_PUBLISHED_PRICE,
  QUARTERS,
  type Quarter
} from '@/config/cbamReportingPeriods'
import { parseEuaPrice } from '@/shared/utils/parseEuaPrice'
import { useCallback, useEffect, useMemo, useState } from 'react'
import cx from 'classnames'

function QuarterOfficialPriceRow({
  quarter,
  checked,
  onCheckedChange,
  isDialogOpen
}: {
  quarter: Quarter
  checked: boolean
  onCheckedChange: (quarter: Quarter, next: boolean) => void
  isDialogOpen: boolean
}) {
  const { currencyFormatter2Decimals, t } = useI18n()

  const isPricePublishedForQuarter =
    OFFICIAL_EUA_2026_QUARTERS_WITH_PUBLISHED_PRICE.includes(quarter)

  const { data, isLoading, isError } = useGetOfficial2026EuaEmissionPrice(quarter, {
    enabled: isDialogOpen && isPricePublishedForQuarter
  })

  const priceDisplay = useMemo(() => {
    if (!isPricePublishedForQuarter) {
      return t('cbamReporting:cbamReportDetails.exportCsvDialog.officialPriceNotYetPublished')
    }
    if (isLoading) {
      return <Spinner className="w-4 inline-block" />
    }
    if (isError || data?.value == null || data.value === '') {
      return '—'
    }
    return currencyFormatter2Decimals(parseEuaPrice(data.value))
  }, [currencyFormatter2Decimals, data?.value, isError, isLoading, isPricePublishedForQuarter, t])

  const isAvailable = useMemo(() => {
    if (!isPricePublishedForQuarter) {
      return false
    }
    if (isLoading) {
      return false
    }
    return !isError && data?.value != null && data.value !== ''
  }, [data?.value, isError, isLoading, isPricePublishedForQuarter])

  const id = `export-csv-quarter-${quarter}`

  return (
    <TableRow className={cx(!isAvailable && !isLoading && 'opacity-60')}>
      <TableCell className="w-12 text-center align-middle">
        <Checkbox
          id={id}
          checked={checked}
          disabled={!isAvailable}
          onChange={next => onCheckedChange(quarter, next)}
        />
      </TableCell>
      <TableCell>
        <span
          className={cx('font-medium', isAvailable ? 'text-text-primary' : 'text-text-secondary')}
        >
          {quarter}
        </span>
      </TableCell>
      <TableCell
        align="right"
        className={cx(
          'min-w-28 tabular-nums',
          !isPricePublishedForQuarter && 'text-text-secondary'
        )}
      >
        {priceDisplay}
      </TableCell>
    </TableRow>
  )
}

export default function ExportCbamReportingCsvDialog({
  isOpen,
  close,
  onConfirm
}: {
  isOpen: boolean
  close: () => void
  onConfirm: (
    quarters: Quarter[],
    format: CbamReportingDataExportFormat
  ) => void | Promise<void>
}) {
  const { t } = useI18n()
  const [selected, setSelected] = useState<Quarter[]>(['Q1'])
  const [format, setFormat] = useState<CbamReportingDataExportFormat>('xlsx')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSelected(['Q1'])
      setFormat('xlsx')
    }
  }, [isOpen])

  const toggleQuarter = useCallback((quarter: Quarter, next: boolean) => {
    setSelected(prev => {
      if (prev.includes(quarter) === next) {
        return prev
      }
      const nextSelection = [...prev]
      if (next) {
        nextSelection.push(quarter)
      } else {
        nextSelection.splice(nextSelection.indexOf(quarter), 1)
      }
      return nextSelection
    })
  }, [])

  const sortedSelectedQuarters = useMemo(() => {
    return QUARTERS.filter(quarterKey => selected.includes(quarterKey))
  }, [selected])

  const canSubmit = sortedSelectedQuarters.length > 0

  const handleExport = async () => {
    if (!canSubmit) {
      return
    }
    setIsSubmitting(true)
    try {
      await onConfirm(sortedSelectedQuarters, format)
      close()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamReporting:cbamReportDetails.exportCsvDialog.title')}
      size="2xl"
    >
      <div className="mb-4">
        <p className="text-text-secondary">
          {t('cbamReporting:cbamReportDetails.exportCsvDialog.description')}
        </p>
      </div>
      <div className="mb-4">
        <RadioGroup<CbamReportingDataExportFormat>
          name="export-format"
          label={t('cbamReporting:cbamReportDetails.exportCsvDialog.format.label')}
          direction="vertical"
          value={format}
          onChange={setFormat}
          options={[
            {
              value: 'xlsx',
              label: t('cbamReporting:cbamReportDetails.exportCsvDialog.format.xlsx')
            },
            {
              value: 'csv',
              label: t('cbamReporting:cbamReportDetails.exportCsvDialog.format.csv')
            }
          ]}
        />
      </div>
      <div className="mb-2">
        <Table className="table-fixed w-full" dense>
          <thead>
            <TableRow>
              <TableColumnHeader className="w-12" />
              <TableColumnHeader>
                {t('cbamReporting:cbamReportDetails.exportCsvDialog.columns.quarter')}
              </TableColumnHeader>
              <TableColumnHeader align="right" className="min-w-28">
                {t('cbamReporting:cbamReportDetails.exportCsvDialog.columns.officialPrice')}
              </TableColumnHeader>
            </TableRow>
          </thead>
          <tbody>
            {QUARTERS.map(quarter => (
              <QuarterOfficialPriceRow
                key={quarter}
                quarter={quarter}
                checked={selected.includes(quarter)}
                onCheckedChange={toggleQuarter}
                isDialogOpen={isOpen}
              />
            ))}
          </tbody>
        </Table>
      </div>
      <footer className="flex justify-end gap-2 mt-4">
        <Button variant="subtle" onClick={close} disabled={isSubmitting}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={!canSubmit || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleExport}
        >
          {t('cbamReporting:cbamReportDetails.exportCsvDialog.actions.export')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
